import React, {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {
    Alert,
    Box,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {loadProposals, ProposalFilter} from "../service/proposal.service";
import {
    Proposal,
    SOURCE_BELARUS_MINSK_ONLINER,
    SOURCE_GEORGIA_BATUMI_KORTER,
    SOURCE_GEORGIA_BATUMI_MYHOME,
} from "../types/Proposal";
import {
    ANALITICS_PROPOSAL_STATUSES,
    buildInvestorAnalitics,
    formatUsdFromCents,
} from "../utils/investorAnalitics";

const ANALITICS_SOURCES = [
    SOURCE_GEORGIA_BATUMI_KORTER,
    SOURCE_GEORGIA_BATUMI_MYHOME,
    SOURCE_BELARUS_MINSK_ONLINER,
] as const;

const ANALITICS_FILTER: ProposalFilter = {
    page: 0,
    size: 50,
    sortKey: "createdAt",
    sortOrder: "desc",
    proposalStatuses: ANALITICS_PROPOSAL_STATUSES,
    light: true,
};

async function loadAnaliticsListings(): Promise<Proposal[]> {
    const collected: Proposal[] = [];
    let lastError: unknown;
    for (const source of ANALITICS_SOURCES) {
        try {
            const response = await loadProposals({...ANALITICS_FILTER, source, page: 0});
            collected.push(...(response.data?.content ?? []));
        } catch (error) {
            lastError = error;
        }
    }
    if (collected.length === 0 && lastError) {
        throw lastError;
    }
    return collected;
}

export function CityAnaliticsPage() {
    const {data, isLoading, error} = useQuery({
        queryKey: ["city-analitics-listings"],
        refetchOnWindowFocus: false,
        queryFn: loadAnaliticsListings,
    });

    const analitics = useMemo(() => buildInvestorAnalitics(data || []), [data]);
    const currentCapital = analitics.capitalByCity.find((row) => row.city === analitics.currentCity);

    return (
        <Box data-testid="city-analitics-page">
            <Typography variant="h5" component="h1" gutterBottom>
                Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                City analitics: demand, liquidity, buyable assets, and purchase capital from existing listings.
            </Typography>

            {isLoading && <Typography>Loading listings…</Typography>}
            {error ? <Alert severity="error">Failed to load listings for analitics.</Alert> : null}

            <Stack spacing={3}>
                <Paper data-testid="district-demand-liquidity" sx={{p: 2}}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        District demand and liquidity
                    </Typography>
                    {analitics.districts.length === 0 ? (
                        <Typography color="text.secondary">
                            No listings with a usable district (address or coordinates).
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>City</TableCell>
                                    <TableCell>District</TableCell>
                                    <TableCell align="right">Listings</TableCell>
                                    <TableCell align="right">Sold</TableCell>
                                    <TableCell align="right">Demand rank</TableCell>
                                    <TableCell align="right">Liquidity rank</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {analitics.districts.map((row) => (
                                    <TableRow key={row.city + row.district}>
                                        <TableCell>{row.city}</TableCell>
                                        <TableCell>{row.district}</TableCell>
                                        <TableCell align="right">{row.listingCount}</TableCell>
                                        <TableCell align="right">{row.soldCount}</TableCell>
                                        <TableCell align="right">{row.demandRank}</TableCell>
                                        <TableCell align="right">{row.liquidityRank}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Paper>

                <Paper data-testid="city-asset-kinds" sx={{p: 2}}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Buyable assets by city
                    </Typography>
                    {analitics.assetsByCity.length === 0 ? (
                        <Typography color="text.secondary">No asset types on current listings.</Typography>
                    ) : (
                        <Stack spacing={2}>
                            {analitics.assetsByCity.map((cityRow) => (
                                <Box key={cityRow.city}>
                                    <Typography variant="subtitle1">{cityRow.city}</Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{mt: 1}}>
                                        {cityRow.kinds.map((kind) => (
                                            <Chip key={kind} label={kind} size="small"/>
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Paper>

                <Paper data-testid="capital-needed" sx={{p: 2}}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Capital needed
                    </Typography>
                    {currentCapital ? (
                        <Typography sx={{mb: 2}}>
                            Current city {currentCapital.city}: entry {formatUsdFromCents(currentCapital.entryPrice)},
                            typical {formatUsdFromCents(currentCapital.typicalPrice)}
                        </Typography>
                    ) : (
                        <Typography color="text.secondary" sx={{mb: 2}}>
                            No priced listings to compute a purchase ticket.
                        </Typography>
                    )}
                    {analitics.capitalByCity.length > 0 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>City</TableCell>
                                    <TableCell align="right">Listings</TableCell>
                                    <TableCell align="right">Entry</TableCell>
                                    <TableCell align="right">Typical</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {analitics.capitalByCity.map((row) => (
                                    <TableRow key={row.city}>
                                        <TableCell>{row.city}</TableCell>
                                        <TableCell align="right">{row.listingCount}</TableCell>
                                        <TableCell align="right">{formatUsdFromCents(row.entryPrice)}</TableCell>
                                        <TableCell align="right">{formatUsdFromCents(row.typicalPrice)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
            </Stack>
        </Box>
    );
}
